import { msalInstance, apiScope } from '../auth/msalConfig';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/admin';

function isJsonResponse(response: Response): boolean {
  const contentType = response.headers.get('content-type') || '';
  return contentType.toLowerCase().includes('application/json');
}

async function readJsonBody(response: Response): Promise<Record<string, unknown>> {
  if (!isJsonResponse(response)) return {};
  return response.json().catch(() => ({}));
}

function getErrorMessage(body: Record<string, unknown>, fallback: string): string {
  const error = body.error;
  return typeof error === 'string' && error.trim() ? error : fallback;
}

async function readResponseSnippet(response: Response): Promise<string> {
  const text = await response.text().catch(() => '');
  return text.slice(0, 120).replace(/\s+/g, ' ').trim();
}

async function expectJsonResponse<T>(response: Response): Promise<T> {
  if (isJsonResponse(response)) {
    return response.json();
  }

  const snippet = await readResponseSnippet(response);
  throw new ApiError(
    response.status || 500,
    `Expected JSON from API, got non-JSON response (${response.status}). Check VITE_API_BASE_URL. Body starts with: ${snippet || '<empty>'}`
  );
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) throw new Error('Not authenticated');

  const response = await msalInstance.acquireTokenSilent({
    scopes: [apiScope],
    account: accounts[0],
  });

  return {
    Authorization: `Bearer ${response.accessToken}`,
    'Content-Type': 'application/json',
  };
}

export async function apiGet<T>(path: string): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}${path}`, { headers });

  if (!response.ok) {
    const body = await readJsonBody(response);
    throw new ApiError(response.status, getErrorMessage(body, 'Request failed'));
  }

  return expectJsonResponse<T>(response);
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const responseBody = await readJsonBody(response);
    throw new ApiError(response.status, getErrorMessage(responseBody, 'Request failed'));
  }

  return expectJsonResponse<T>(response);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const responseBody = await readJsonBody(response);
    throw new ApiError(response.status, getErrorMessage(responseBody, 'Request failed'));
  }

  return expectJsonResponse<T>(response);
}

export async function apiDelete(path: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const body = await readJsonBody(response);
    throw new ApiError(response.status, getErrorMessage(body, 'Request failed'));
  }
}

export async function apiPostFile<T>(path: string, file: File): Promise<T> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) throw new Error('Not authenticated');

  const response = await msalInstance.acquireTokenSilent({
    scopes: [apiScope],
    account: accounts[0],
  });

  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${response.accessToken}`,
    },
    body: formData,
  });

  if (!res.ok) {
    const responseBody = await readJsonBody(res);
    throw new ApiError(res.status, getErrorMessage(responseBody, 'Upload failed'));
  }

  return expectJsonResponse<T>(res);
}

export async function apiDownload(path: string, filename: string): Promise<void> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) throw new Error('Not authenticated');

  const tokenResponse = await msalInstance.acquireTokenSilent({
    scopes: [apiScope],
    account: accounts[0],
  });

  const response = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
  });

  if (!response.ok) {
    const body = await readJsonBody(response);
    throw new ApiError(response.status, getErrorMessage(body, 'Download failed'));
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}
